import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PERSISTENCE_KEY = '@navigation/active_screen';

export interface NavigationState {
  screen: string;
  params?: Record<string, any>;
}

const TICKET_DETAIL_PATH_REGEX = /^\/ticket-details\/ticket-id\/([^/]+)\/?$/i;

/**
 * Build canonical web pathname for supported screens.
 * Returns null for screens that still use hash-based URLs.
 */
export function buildWebPath(screen: string, params?: Record<string, any>): string | null {
  if (screen === 'Ticket Detail') {
    const ticketId = String(params?.ticketId ?? '').trim();
    if (!ticketId) return null;
    return `/ticket-details/ticket-id/${encodeURIComponent(ticketId)}`;
  }

  // Navigator report screens already have path-like names (e.g. /navigator-reports/perfect-truck-analytics)
  // Use them directly as the clean web pathname without hash encoding.
  if (screen.startsWith('/navigator-reports/')) {
    return screen;
  }

  return null;
}

/**
 * Parse supported web pathname routes into deep-link screen strings.
 * Example: /ticket-details/ticket-id/NTI-2026-00036
 *          -> "Ticket Detail?ticketId=NTI-2026-00036"
 */
function parsePathToDeepLink(pathname: string): string | null {
  const ticketDetailMatch = pathname.match(TICKET_DETAIL_PATH_REGEX);
  if (ticketDetailMatch?.[1]) {
    return buildDeepLink('Ticket Detail', { ticketId: decodeURIComponent(ticketDetailMatch[1]) });
  }

  // Navigator report screens: the pathname IS the screen name
  if (pathname.startsWith('/navigator-reports/')) {
    return pathname;
  }

  return null;
}

/**
 * Restores the last active screen + params from storage.
 *
 * Strategy (in priority order):
 * 1. Web: read from pathname route for supported clean URLs.
 * 2. Web: read from URL hash (legacy + fallback behavior).
 * 3. All platforms: fall back to AsyncStorage.
 *
 * Returns null if nothing is saved (app should default to Home).
 */
export async function restoreLastScreen(): Promise<string | null> {
  try {
    // Web: path-based deep links take precedence (clean URLs without hash)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathDeepLink = parsePathToDeepLink(window.location.pathname);
      if (pathDeepLink) {
        return pathDeepLink;
      }

      // Hash fallback for existing URLs
      const hash = window.location.hash.slice(1);
      if (hash) {
        return decodeURIComponent(hash);
      }
    }

    // All platforms (iOS, Android, Web fallback): AsyncStorage
    return await AsyncStorage.getItem(PERSISTENCE_KEY);
  } catch {
    return null;
  }
}

/**
 * Extracts query params from a deep link URL.
 * Example: "Ticket Detail?ticketId=NTI-2026-00022" → { ticketId: "NTI-2026-00022" }
 */
export function parseDeepLinkParams(screenStr: string): { screen: string; params: Record<string, string> } {
  const [screen, queryStr] = screenStr.split('?');
  const params: Record<string, string> = {};

  if (queryStr) {
    const pairs = queryStr.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }
  }

  return { screen: screen.trim(), params };
}

/**
 * Builds a deep link URL string with optional query params.
 * Example: buildDeepLink("Ticket Detail", { ticketId: "NTI-2026-00022" })
 *          → "Ticket Detail?ticketId=NTI-2026-00022"
 */
export function buildDeepLink(screen: string, params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return screen;
  }

  const queryPairs = Object.entries(params)
    .filter(([, val]) => val != null)
    .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`)
    .join('&');

  return queryPairs ? `${screen}?${queryPairs}` : screen;
}

/**
 * Persists the active screen + params to storage as a deep link.
 *
 * On web, uses history.replaceState to update the URL hash without
 * creating a new browser history entry (avoids polluting the back stack).
 * On all platforms, also writes to AsyncStorage as the canonical store.
 */
export async function saveScreen(screen: string, params?: Record<string, any>): Promise<void> {
  try {
    const deepLink = buildDeepLink(screen, params);

    // Web: update URL first so a quick refresh restores the latest screen + params.
    // Prefer clean pathname routes where supported, otherwise use hash fallback.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const webPath = buildWebPath(screen, params);
      if (webPath) {
        window.history.replaceState(null, '', webPath);
      } else {
        // Always use root pathname so a previously-set clean path (e.g.
        // /navigator-reports/perfect-truck-analytics) does not linger in the
        // URL after the user navigates away. Without the leading '/', the
        // browser keeps the old pathname and restoreLastScreen would wrongly
        // restore the previous clean-path screen on refresh.
        window.history.replaceState(null, '', `/#${encodeURIComponent(deepLink)}`);
      }
    }

    // Persist in AsyncStorage as canonical cross-platform storage.
    // Do not block navigation on this async write.
    void AsyncStorage.setItem(PERSISTENCE_KEY, deepLink);
  } catch {
    // Ignore storage errors — navigation still works, just won't persist
  }
}
